import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { profile } from "@/data/resume";

const links = [
  { label: "About", href: "#about" },
  { label: "Thoughts & Reflections", href: "#film" },
  { label: "Experience", href: "#work" },
  { label: "Project Portfolio", href: "#projects" },
  { label: "Writing", href: "#writing" },
  { label: "Contact", href: "#contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled ? "backdrop-blur-xl bg-[#FBFAF7]/85 border-b border-black/10" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-[1500px] items-center justify-between px-6 py-5 md:px-12">
        <button
          onClick={() => go("#top")}
          data-testid="nav-logo"
          aria-label="Back to top"
          className="h-6 w-6"
        />

        <ul className="hidden items-center gap-6 lg:flex">
          {links.map((l) => (
            <li key={l.href}>
              <button
                onClick={() => go(l.href)}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className="whitespace-nowrap font-mono-x text-[11px] uppercase tracking-[0.15em] text-[#6E6D66] transition-colors hover:text-[#1A1A18]"
              >
                {l.label}
              </button>
            </li>
          ))}
        </ul>

        <a
          href={`mailto:${profile.email}`}
          data-testid="nav-cta"
          className="hidden rounded-full border border-black/20 px-5 py-2 font-mono-x text-xs uppercase tracking-[0.2em] text-[#1A1A18] transition-colors hover:border-[#9A7526] hover:text-[#9A7526] lg:block"
        >
          Get in touch
        </a>

        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          className="text-[#1A1A18] lg:hidden"
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="overflow-hidden border-t border-black/10 bg-[#FBFAF7]/97 backdrop-blur-xl lg:hidden"
          >
            <ul className="flex flex-col px-6 py-4">
              {links.map((l) => (
                <li key={l.href}>
                  <button
                    onClick={() => go(l.href)}
                    data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                    className="w-full py-3 text-left font-display text-2xl text-[#1A1A18]"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
